import { Injectable, inject, signal, computed } from '@angular/core';
import { Subject, Subscription, filter, timer } from 'rxjs';
import { MessagingService } from './messaging.service';
import { MessageDto, MessageChannel, MessageType } from '../interfaces/message-dto';

export interface PasswordResetNotification {
    type: MessageType | null;
    message: string | null;
    alertType: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class PasswordResetNotificationService {
    private readonly messagingService = inject(MessagingService);

    private subscription: Subscription | null = null;
    private subscribedEnterpriseId: number | null = null;
    private infoDisplayedAt: number | null = null;
    private readonly minInfoDisplayMs = 2000;

    private readonly notificationSignal = signal<PasswordResetNotification>({
        type: null,
        message: null,
        alertType: null
    });

    readonly notification = this.notificationSignal.asReadonly();
    readonly isProcessing = computed(() => this.notificationSignal().type === MessageType.INFO);

    private readonly messagesSubject = new Subject<MessageDto>();
    readonly messages$ = this.messagesSubject.asObservable();

    /**
     * Subscribe to password reset notifications for an enterprise.
     * This should be called once at the app level to maintain the subscription
     * across component lifecycle changes.
     */
    subscribeToPasswordResetNotifications(enterpriseId: number): void {
        // Skip if already subscribed to the same enterprise
        if (this.subscription && this.subscribedEnterpriseId === enterpriseId) {
            return;
        }

        this.unsubscribe();
        this.subscribedEnterpriseId = enterpriseId;

        this.subscription = this.messagingService
            .watchEnterpriseMessages(enterpriseId)
            .pipe(filter((msg) => this.isPasswordResetMessage(msg)))
            .subscribe({
                next: (msg) => this.handleMessage(msg),
                error: (err) => this.handleError(err)
            });
    }

    subscribeToGlobalPasswordResetNotifications(): void {
        if (this.subscription && this.subscribedEnterpriseId === null) {
            return;
        }

        this.unsubscribe();

        this.subscription = this.messagingService
            .watchGlobalMessages()
            .pipe(filter((msg) => this.isPasswordResetMessage(msg)))
            .subscribe({
                next: (msg) => this.handleMessage(msg),
                error: (err) => this.handleError(err)
            });
    }

    /**
     * Unsubscribe from notifications.
     * This should only be called when the user logs out or the app is destroyed.
     */
    unsubscribe(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
            this.subscribedEnterpriseId = null;
        }
    }

    /**
     * Reset the notification state.
     * Call this before starting a new password reset operation.
     */
    reset(): void {
        this.notificationSignal.set({ type: null, message: null, alertType: null });
    }

    private isPasswordResetMessage(msg: MessageDto): boolean {
        return msg.channel === MessageChannel.PASSWORD_RESET;
    }

    private handleMessage(msg: MessageDto): void {
        this.messagesSubject.next(msg);

        switch (msg.type) {
            case MessageType.INFO:
                this.infoDisplayedAt = Date.now();
                this.notificationSignal.set({
                    type: MessageType.INFO,
                    message: msg.message,
                    alertType: 'tier2'
                });
                break;
            case MessageType.UPDATE:
                this.setSignalAfterMinDisplay({
                    type: MessageType.UPDATE,
                    message: msg.message,
                    alertType: 'success'
                });
                break;
            case MessageType.ERROR:
                this.setSignalAfterMinDisplay({
                    type: MessageType.ERROR,
                    message: msg.message,
                    alertType: 'error'
                });
                break;
            case MessageType.WARNING:
                this.setSignalAfterMinDisplay({
                    type: MessageType.WARNING,
                    message: msg.message,
                    alertType: 'tier3'
                });
                break;
        }
    }

    private setSignalAfterMinDisplay(notification: PasswordResetNotification): void {
        const elapsed = this.infoDisplayedAt ? Date.now() - this.infoDisplayedAt : this.minInfoDisplayMs;
        const remaining = Math.max(0, this.minInfoDisplayMs - elapsed);
        this.infoDisplayedAt = null;

        if (remaining === 0) {
            this.notificationSignal.set(notification);
        } else {
            timer(remaining).subscribe(() => this.notificationSignal.set(notification));
        }
    }

    private handleError(error: unknown): void {
        console.error('Password reset notification error:', error);
        this.notificationSignal.set({
            type: MessageType.ERROR,
            message: 'Connection error occurred while receiving notifications.',
            alertType: 'error'
        });
    }
}
